import styled from 'styled-components';

export const Button = styled.button`
  padding: ${props => props.$size === 'large' ? '14px 40px' : '10px 24px'};
  font-size: ${props => props.$size === 'large' ? '0.9rem' : '0.8rem'};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid transparent;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: ${props.theme.colors.primary};
          color: white;
          box-shadow: ${props.theme.shadows.sm};
          &:hover {
            background: ${props.theme.colors.secondary};
            box-shadow: ${props.theme.shadows.md};
          }
        `;
      case 'secondary':
        return `
          background: transparent;
          border: 1px solid ${props.theme.colors.primary};
          color: ${props.theme.colors.primary};
          &:hover {
            background: ${props.theme.colors.primary};
            color: white;
          }
        `;
      case 'outline':
        return `
          background: transparent;
          border: 1px solid ${props.theme.colors.secondary};
          color: ${props.theme.colors.secondary};
          &:hover {
            background: ${props.theme.colors.secondary};
            color: white;
          }
        `;
      case 'ghost':
        return `
          color: ${props.theme.colors.text.secondary};
          &:hover {
            color: ${props.theme.colors.primary};
          }
        `;
      default:
        return `
          border-bottom: 1px solid ${props.theme.colors.primary};
          padding: 5px 0;
          border-radius: 0;
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 12px 0;
  font-size: 1rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.main};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  outline: none;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:focus {
    border-bottom-color: ${({ theme }) => theme.colors.primary};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
    opacity: 0.5;
  }
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.background};
  padding: ${props => props.$padding || '0'};
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  
  ${props => props.$hover && `
    &:hover {
      box-shadow: ${props.theme.shadows.md};
      transform: translateY(-5px);
    }
  `}
`;

export const Container = styled.div`
  max-width: ${props => props.$maxWidth || '1200px'};
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${props => props.$minWidth || '280px'}, 1fr));
  gap: ${props => props.$gap || '1.5rem'};
`;

export const Flex = styled.div`
  display: flex;
  flex-direction: ${props => props.$direction || 'row'};
  justify-content: ${props => props.$justify || 'flex-start'};
  align-items: ${props => props.$align || 'stretch'};
  gap: ${props => props.$gap || '0'};
  flex-wrap: ${props => props.$wrap || 'nowrap'};
`;

export const Text = styled.p`
  margin: ${props => props.$margin || '0'};
  font-size: ${props => props.$size || '14px'};
  font-weight: ${props => props.$weight || '400'};
  color: ${props => props.$color || props.theme.colors.text.secondary};
  line-height: 1.6;
`;

export const Heading = styled.h2`
  font-size: ${props => {
    switch (props.$level) {
      case 1: return '2.5rem';
      case 2: return '2rem';
      case 3: return '1.5rem';
      default: return '1.25rem';
    }
  }};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.main};
  margin: ${props => props.$margin || '0 0 1rem 0'};
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${props => {
    switch (props.$variant) {
      case 'success': return 'rgba(16, 185, 129, 0.2)';
      case 'warning': return 'rgba(245, 158, 11, 0.2)';
      case 'danger': return 'rgba(239, 68, 68, 0.2)';
      case 'info': return 'rgba(59, 130, 246, 0.2)';
      default: return props.theme.colors.surfaceHighlight;
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'success': return '#34d399';
      case 'warning': return '#fbbf24';
      case 'danger': return '#f87171';
      case 'info': return '#60a5fa';
      default: return props.theme.colors.text.main;
    }
  }};
`;

export const Spinner = styled.div`
  border: 3px solid ${({ theme }) => theme.colors.surfaceHighlight};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  width: ${props => props.$size || '40px'};
  height: ${props => props.$size || '40px'};
  animation: spin 1s linear infinite;
  margin: ${props => props.$center ? '40px auto' : '0'};
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
